import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Save, Send } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const EditJobOfferModal = ({ isOpen, onClose, onSuccess, offer }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: offer?.title || '',
    description: offer?.description || '',
    qualites_attendues: offer?.qualites_attendues || '',
    lieu_travail: offer?.lieu_travail || '',
    date_arrivee: offer?.date_arrivee || '',
    type_poste: offer?.type_poste || '',
    taux_horaire: offer?.taux_horaire || ''
  });

  if (!isOpen || !offer) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (status = 'en_attente') => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('L\'intitulé du poste est requis');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La description des missions est requise');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const jobOfferData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        company: user.company,
        status: status,
        qualites_attendues: formData.qualites_attendues.trim() || null,
        lieu_travail: formData.lieu_travail.trim() || null,
        date_arrivee: formData.date_arrivee || null,
        type_poste: formData.type_poste || null,
        taux_horaire: formData.taux_horaire ? parseFloat(formData.taux_horaire) : null
      };

      const response = await fetch(`${API}/job-offers/${offer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jobOfferData),
      });

      if (response.ok) {
        toast.success(status === 'brouillon' ? 'Offre sauvegardée en brouillon' : 'Offre modifiée et renvoyée pour validation');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la modification de l\'offre');
      }
    } catch (error) {
      console.error('Error updating job offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    handleSubmit('brouillon');
  };

  const handleSendForValidation = () => {
    handleSubmit('en_attente');
  };

  return (
    <div className="job-offer-modal-overlay">
      <div className="job-offer-modal">
        <Card className="job-offer-modal-card">
          <CardHeader className="job-offer-modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="job-offer-modal-title">
                Modifier l'offre d'emploi
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="job-offer-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="job-offer-modal-content">
            <form className="job-offer-form" onSubmit={(e) => e.preventDefault()}>
              {/* Intitulé du poste */}
              <div className="form-group">
                <Label htmlFor="title" className="form-label">
                  Intitulé du poste *
                </Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ex: Développeur Full Stack"
                />
              </div>

              {/* Description des missions */}
              <div className="form-group">
                <Label htmlFor="description" className="form-label">
                  Description des missions *
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Décrivez les missions principales du poste..."
                  rows={4}
                />
              </div>

              {/* Qualités attendues */}
              <div className="form-group">
                <Label htmlFor="qualites_attendues" className="form-label">
                  Qualités attendues
                </Label>
                <Textarea
                  id="qualites_attendues"
                  name="qualites_attendues"
                  value={formData.qualites_attendues}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Listez les qualités et compétences recherchées..."
                  rows={3}
                />
              </div>

              {/* Lieu de travail */}
              <div className="form-group">
                <Label htmlFor="lieu_travail" className="form-label">
                  Lieu de travail
                </Label>
                <Input
                  id="lieu_travail"
                  name="lieu_travail"
                  value={formData.lieu_travail}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ex: Paris, Télétravail partiel"
                />
              </div>

              {/* Date idéale d'arrivée */}
              <div className="form-group">
                <Label htmlFor="date_arrivee" className="form-label">
                  Date idéale d'arrivée
                </Label>
                <Input
                  id="date_arrivee"
                  name="date_arrivee"
                  type="date"
                  value={formData.date_arrivee}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              {/* Horizon Solution specific fields */}
              {user?.company === 'Horizon Solution' && (
                <>
                  {/* Type de poste */}
                  <div className="form-group">
                    <Label htmlFor="type_poste" className="form-label">
                      Type de poste
                    </Label>
                    <select
                      id="type_poste"
                      name="type_poste"
                      value={formData.type_poste}
                      onChange={handleInputChange}
                      className="form-input"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e4e4e7',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: 'white',
                        color: '#0a0a0a'
                      }}
                    >
                      <option value="">Sélectionnez un type de poste</option>
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="Alternance">Alternance</option>
                      <option value="Stage">Stage</option>
                    </select>
                  </div>

                  {/* Taux horaire */}
                  <div className="form-group">
                    <Label htmlFor="taux_horaire" className="form-label">
                      Taux horaire (en €)
                    </Label>
                    <Input
                      id="taux_horaire"
                      name="taux_horaire"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.taux_horaire}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Ex: 15.50"
                    />
                  </div>
                </>
              )}

              {/* Action buttons */}
              <div className="job-offer-actions">
                <Button
                  onClick={handleSendForValidation}
                  disabled={loading}
                  className="btn-primary"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Send size={16} className="mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer'}
                </Button>
                <Button
                  onClick={handleSaveDraft}
                  disabled={loading}
                  variant="outline"
                  className="btn-outline"
                  style={{ borderColor: '#f7bc46', color: '#f7bc46' }}
                >
                  <Save size={16} className="mr-2" />
                  Enregistrer
                </Button>
                <Button
                  onClick={onClose}
                  disabled={loading}
                  variant="outline"
                  className="btn-cancel"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditJobOfferModal;